import { closestCenter, DndContext, useDroppable, type DragEndEvent } from "@dnd-kit/core";
import { SortableContext, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { ChevronDown, ChevronUp, GripVertical, Plus, X } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useCatalogDragSensors } from "./drag-sensors";

/**
 * The shell every catalog section shares: a titled card, a row per item with
 * reorder/remove controls, an add button and an empty state.
 *
 * Row order is real data — it persists to `display_order` and is the order the
 * agent reads its options in — so reordering is a first-class control rather
 * than an incidental one. Follows the ▲▼ pattern already in ManageColumnsModal
 * instead of introducing drag, which is hard to operate on touch.
 *
 * It owns no data. Every row saves itself the moment it is edited (see
 * `useCatalogEditor`), so the list only reports intent: add, remove, move.
 */
export type EditableRowListProps<T> = {
  title: string;
  /**
   * Replaces the title row — a category card puts its editable name, its order
   * arrows and its own menu there. The count badge goes with it.
   */
  header?: React.ReactNode;
  /** Explains a non-obvious rule. Rendered above the rows. */
  hint?: React.ReactNode;
  items: T[];
  /** A key that survives saves and reorders — never the index. */
  getKey: (item: T) => string;
  renderRow: (item: T, index: number) => React.ReactNode;
  /** Validation messages under the row, already filtered to what should show. */
  rowErrors?: (item: T) => string[];
  /** Whatever else belongs under the row: a failed save, a notice with an action. */
  renderRowFooter?: (item: T) => React.ReactNode;
  /**
   * A fixed-width slot before the menu for the row's save state (spinner, ✓).
   * Inline on purpose: a line under the row would make it grow and shrink on
   * every save.
   */
  renderRowStatus?: (item: T) => React.ReactNode;
  /**
   * Replaces the row's X with a menu of its own (products get "⋯"). ▲▼ stay:
   * reordering is frequent and positional.
   */
  renderRowMenu?: (item: T) => React.ReactNode;
  /** The X. Whether it asks first is the caller's call. */
  onRemove: (item: T) => void;
  /** Called after ▲/▼ with the reordered list; the order is saved right there. */
  onReorder: (items: T[]) => void;
  /** A row not saved yet has no place in a saved order. */
  canMove?: (item: T) => boolean;
  onAdd: () => void;
  /**
   * When set, the add button opens these choices instead of adding at once — a
   * school picks whether the new program runs by cycle or on its own dates.
   */
  addOptions?: Array<{ label: string; onSelect: () => void }>;
  /** Why the add button is disabled, said next to it (a category not saved yet). */
  addDisabledReason?: string;
  /** Errors about the list as a whole rather than one row. */
  listErrors?: string[];
  addLabel: string;
  emptyLabel: string;
  /** Names the row in the controls' accessible labels. */
  rowName: (item: T, index: number) => string;
  /** Rendered between the rows and the add button — e.g. the relation strip. */
  footer?: React.ReactNode;
  disabled?: boolean;
  /**
   * Opts this list into drag and drop, under a `DndContext` the caller owns:
   * the list becomes a drop target with this id, and each movable row gets a
   * grip handle. Products use this — they can be dropped into another group.
   */
  dropGroupId?: string;
  /**
   * Drag and drop inside this list only, with a context of its own (cycles,
   * custom fields). Either way, a list that drags shows the grip INSTEAD of
   * the arrows: one control to order per screen size. Unset (a phone, the
   * wizard) = arrows only.
   */
  sortable?: boolean;
};

/** The grip's footprint on a row that cannot move yet, so every row's inputs line up. */
function GripSpacer(): React.JSX.Element {
  return <span aria-hidden className="-ml-2 mt-1.5 w-[18px] shrink-0" />;
}

/**
 * One row's outer frame. Sortable when it has a `sortId` — which only happens
 * inside a `DndContext` — and plain otherwise, so the list works with or
 * without drag and drop. The grip is the only drag surface: the row is full of
 * inputs, and dragging from anywhere would fight text selection.
 */
function RowFrame({
  sortId,
  label,
  children,
}: {
  sortId: string | undefined;
  label: string;
  children: (handle: React.ReactNode) => React.ReactNode;
}): React.JSX.Element {
  return sortId ? (
    <SortableFrame sortId={sortId} label={label}>
      {children}
    </SortableFrame>
  ) : (
    <div className="border-b border-ink-050 last:border-b-0">{children(null)}</div>
  );
}

function SortableFrame({
  sortId,
  label,
  children,
}: {
  sortId: string;
  label: string;
  children: (handle: React.ReactNode) => React.ReactNode;
}): React.JSX.Element {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } =
    useSortable({ id: sortId });
  // dnd-kit positions the row by transform; the Kanban cards do the same
  // (KanbanCard.tsx) — the one place an inline style is the mechanism itself.
  const style = transform
    ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`, transition }
    : undefined;
  const handle = (
    <button
      type="button"
      ref={setActivatorNodeRef}
      aria-label={`Arrastrar ${label}`}
      className="-ml-2 mt-1.5 shrink-0 cursor-grab touch-none rounded p-0.5 text-ink-300 hover:bg-ink-050 hover:text-ink-700 active:cursor-grabbing"
      {...attributes}
      {...listeners}
    >
      <GripVertical size={14} />
    </button>
  );
  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`border-b border-ink-050 bg-white last:border-b-0 ${isDragging ? "relative z-10 opacity-60 shadow-md" : ""}`}
    >
      {children(handle)}
    </div>
  );
}

/** The whole card as a drop target — the only way into an EMPTY group. */
function DropArea({ id, children }: { id: string; children: React.ReactNode }): React.JSX.Element {
  const { setNodeRef, isOver } = useDroppable({ id });
  return (
    <div ref={setNodeRef} className={isOver ? "rounded-lg ring-2 ring-lime" : ""}>
      {children}
    </div>
  );
}

export function EditableRowList<T>({
  title,
  header,
  hint,
  items,
  getKey,
  renderRow,
  rowErrors,
  renderRowFooter,
  renderRowStatus,
  renderRowMenu,
  onRemove,
  onReorder,
  canMove = () => true,
  onAdd,
  addOptions,
  addDisabledReason,
  listErrors = [],
  addLabel,
  emptyLabel,
  rowName,
  footer,
  disabled = false,
  dropGroupId,
  sortable = false,
}: EditableRowListProps<T>): React.JSX.Element {
  const dragMode = dropGroupId !== undefined || sortable;
  const sensors = useCatalogDragSensors();
  function move(index: number, delta: number): void {
    const target = index + delta;
    if (target < 0 || target >= items.length) return;
    const next = [...items];
    const [moved] = next.splice(index, 1);
    if (moved === undefined) return;
    next.splice(target, 0, moved);
    onReorder(next);
  }

  const sortIds = dragMode ? items.filter((item) => canMove(item)).map(getKey) : [];

  function handleOwnDragEnd({ active, over }: DragEndEvent): void {
    if (!over || active.id === over.id) return;
    const from = items.findIndex((item) => getKey(item) === active.id);
    const to = items.findIndex((item) => getKey(item) === over.id);
    if (from < 0 || to < 0) return;
    move(from, to - from);
  }

  const section = (
    <section className="rounded-lg border border-ink-100 bg-white" aria-label={title}>
      {header ?? (
        <div className="flex flex-wrap items-center gap-2 px-4 pb-3 pt-3.5">
          <h4 className="text-sm font-semibold text-ink-900">{title}</h4>
          <span className="rounded-full bg-ink-050 px-2 py-0.5 text-[11px] font-semibold tabular-nums text-ink-500">
            {items.length}
          </span>
        </div>
      )}

      {hint && (
        <p className="max-w-[70ch] px-4 pb-3 text-[12.5px] leading-relaxed text-ink-500">
          {hint}
        </p>
      )}

      {items.length === 0 ? (
        <p className="border-t border-ink-050 px-4 py-4 text-[13px] text-ink-300">
          {emptyLabel}
        </p>
      ) : (
        <div className="border-t border-ink-050">
          {items.map((item, index) => {
            const errors = rowErrors?.(item) ?? [];
            const movable = canMove(item);
            const neighbourAbove = items[index - 1];
            const neighbourBelow = items[index + 1];
            return (
              <RowFrame
                key={getKey(item)}
                sortId={dragMode && movable ? getKey(item) : undefined}
                label={rowName(item, index)}
              >
                {(handle) => (
                  <>
                    <div className="flex items-start gap-2 px-4 py-2.5">
                      {dragMode && (handle ?? <GripSpacer />)}
                      {/* Order on the LEFT, stacked: where a reorder handle is looked
                          for, and ~20px of width instead of ~50 — taken from the name
                          field, which is the one that runs out of room first. Actions
                          on the product stay on the right. */}
                      {!dragMode && (
                        <div className="-ml-1.5 flex shrink-0 flex-col">
                          <button
                            type="button"
                            aria-label={`Subir ${rowName(item, index)}`}
                            disabled={
                              disabled ||
                              !movable ||
                              neighbourAbove === undefined ||
                              !canMove(neighbourAbove)
                            }
                            onClick={() => move(index, -1)}
                            className="rounded p-0.5 text-ink-300 hover:bg-ink-050 hover:text-ink-700 disabled:pointer-events-none disabled:opacity-30"
                          >
                            <ChevronUp size={14} />
                          </button>
                          <button
                            type="button"
                            aria-label={`Bajar ${rowName(item, index)}`}
                            disabled={
                              disabled ||
                              !movable ||
                              neighbourBelow === undefined ||
                              !canMove(neighbourBelow)
                            }
                            onClick={() => move(index, 1)}
                            className="rounded p-0.5 text-ink-300 hover:bg-ink-050 hover:text-ink-700 disabled:pointer-events-none disabled:opacity-30"
                          >
                            <ChevronDown size={14} />
                          </button>
                        </div>
                      )}
                      <div className="flex min-w-0 flex-1 flex-wrap gap-2">
                        {renderRow(item, index)}
                      </div>
                      <div className="flex items-center gap-px pt-1">
                        {renderRowStatus && (
                          <span className="flex h-6 w-5 items-center justify-center">
                            {renderRowStatus(item)}
                          </span>
                        )}
                        {renderRowMenu ? (
                          renderRowMenu(item)
                        ) : (
                          <button
                            type="button"
                            aria-label={`Quitar ${rowName(item, index)}`}
                            disabled={disabled}
                            onClick={() => onRemove(item)}
                            className="rounded p-1 text-ink-300 hover:bg-red-50 hover:text-red-600 disabled:pointer-events-none disabled:opacity-30"
                          >
                            <X size={14} />
                          </button>
                        )}
                      </div>
                    </div>
                    {errors.map((message) => (
                      <p key={message} className="px-4 pb-2 text-[12px] text-red-600">
                        {message}
                      </p>
                    ))}
                    {renderRowFooter?.(item)}
                  </>
                )}
              </RowFrame>
            );
          })}
        </div>
      )}

      {listErrors.map((message) => (
        <p key={message} className="border-t border-ink-050 px-4 pt-2 text-[12px] text-red-600">
          {message}
        </p>
      ))}

      {footer}

      <div className="flex flex-wrap items-center gap-x-2">
        {addOptions ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                disabled={disabled || addDisabledReason !== undefined}
                className="flex items-center gap-1.5 px-4 py-3 text-[12.5px] font-semibold text-moss hover:underline disabled:pointer-events-none disabled:opacity-40"
              >
                <Plus size={13} /> {addLabel}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              {addOptions.map((option) => (
                <DropdownMenuItem key={option.label} onSelect={option.onSelect}>
                  {option.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        ) : (
          <button
            type="button"
            disabled={disabled || addDisabledReason !== undefined}
            onClick={onAdd}
            className="flex items-center gap-1.5 px-4 py-3 text-[12.5px] font-semibold text-moss hover:underline disabled:pointer-events-none disabled:opacity-40"
          >
            <Plus size={13} /> {addLabel}
          </button>
        )}
        {addDisabledReason && (
          <span className="-ml-2 text-[12px] text-ink-400">{addDisabledReason}</span>
        )}
      </div>
    </section>
  );

  if (sortable) {
    return (
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleOwnDragEnd}>
        <SortableContext items={sortIds} strategy={verticalListSortingStrategy}>
          {section}
        </SortableContext>
      </DndContext>
    );
  }
  if (!dropGroupId) return section;
  return (
    <DropArea id={dropGroupId}>
      <SortableContext items={sortIds} strategy={verticalListSortingStrategy}>
        {section}
      </SortableContext>
    </DropArea>
  );
}
